import { FeedbackEditorTextfield } from "./FeedbackEditorTextfield";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Add,
  Delete,
  ExpandMore,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { useApiClient } from "../../../api";
import GeneralLoading, {
  FruitifyLoading,
} from "../../../shared-components/Loading";
import { formatDistanceToNow } from "date-fns";
import Tooltip from "@mui/material/Tooltip";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";
import { CreateWithBudButton } from "../../assistant/components/CreateWithBudButton";
import { DismissCanonicalEntityDialog } from "./DismissCanonicalEntityDialog";
import { enqueueSnackbar } from "notistack";
import { LinkCanonicalEntityDialog } from "./LinkCanonicalEntityDialog";

export { FeedbackEditorTextfield } from "./FeedbackEditorTextfield";

const getEntityIcon = (type) => {
  switch (type) {
    case "misconception":
      return <ThumbDown fontSize="small" color="error" />;
    case "skill":
      return <ThumbUp fontSize="small" color="success" />;
    case "knowledge_gap":
      return <ThumbDown fontSize="small" color="error" />;
    default:
      return <ThumbDown fontSize="small" />;
  }
};

export const CanonicalEntityInsights = ({
  canonicalEntityInsights,
  assignmentQuestion = null,
  assignment = null,
  student = false,
  onNewEntityLink = () => {},
}) => {
  const [insights, setInsights] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [dismissEntityDialogOpen, setDismissEntityDialogOpen] = useState(false);
  const [linkEntityDialogOpen, setLinkEntityDialogOpen] = useState(false);
  const ApiClient = useApiClient();
  // const [canonicalEntityInsights, setCanonicalEntityInsights] = useState(
  //   answer?.canonicalEntityInsights || [],
  // );

  useEffect(() => {
    setInsights(canonicalEntityInsights);
  }, [canonicalEntityInsights]);

  const dismissCanonicalEntity = async () => {
    try {
      const id = selectedEntity.associatedObjectId;

      const response = await ApiClient.skill.dismissCanonicalEntity({
        learningEvidence: student ? id : null,
        linkedCanonicalEntity: student ? null : id,
      });
      if (response.success) {
        setInsights((prev) =>
          prev.filter((item) => item.associatedObjectId !== id),
        );
        enqueueSnackbar(response.message, {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar("Failed to remove", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to update Feedback", error);
    } finally {
      setDismissEntityDialogOpen(false);
    }
  };

  const handleDismissItemClicked = (e, item) => {
    e.stopPropagation();
    setSelectedEntity(item);
    setDismissEntityDialogOpen(true);
  };

  const orderedCanonicalEntityInsights = useMemo(() => {
    return [...insights].sort((a, b) => {
      if (a.processed === b.processed) return 0;

      return a.processed ? -1 : 1;
    });
  }, [insights]);
  const quizAssignment = assignment || assignmentQuestion?.assignment || null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 3,
        alignItems: "center",
        width: "100%",
        mt: 2,
      }}
    >
      <DismissCanonicalEntityDialog
        open={dismissEntityDialogOpen}
        setOpen={setDismissEntityDialogOpen}
        onConfirm={dismissCanonicalEntity}
      />
      <LinkCanonicalEntityDialog
        open={linkEntityDialogOpen}
        setOpen={setLinkEntityDialogOpen}
        entityRequired={!student}
        onConfirm={onNewEntityLink}
      />
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Learning Evidence
          </Typography>
          <Button
            variant={"contained"}
            endIcon={<Add />}
            onClick={() => setLinkEntityDialogOpen(true)}
          >
            Add
          </Button>
        </Stack>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {orderedCanonicalEntityInsights.map((item) => (
            <Tooltip
              key={item.id}
              title={
                item.processed
                  ? ""
                  : "We are still processing this and it is subject to change"
              }
            >
              <Accordion
                disabled={!item.processed}
                sx={{
                  m: "0 !important",
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  bgcolor: "transparent",
                  overflow: "hidden",
                  "&::before": { display: "none" },
                  "&.Mui-disabled": {
                    opacity: 1,
                    bgcolor: "transparent",
                  },
                  "& .MuiAccordionSummary-root.Mui-disabled": {
                    opacity: 0.35,
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    {getEntityIcon(item.entityType)}

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Box>

                    {!item.processed && (
                      <GeneralLoading
                        size={16}
                        direction={"row"}
                        message={"Processing..."}
                      />
                    )}
                  </Box>
                  {item.processed && (
                    <IconButton
                      component="span"
                      role="button"
                      tabIndex={0}
                      sx={{ border: 0 }}
                      onClick={(e) => {
                        handleDismissItemClicked(e, item);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleDismissItemClicked(e, item);
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </AccordionSummary>

                <AccordionDetails>
                  {item.explanation && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Explanation: </strong>
                      {item.explanation}
                    </Typography>
                  )}
                  {item.supportingText && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Supporting text: </strong>
                      {item.supportingText}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Tooltip>
          ))}
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
            mt: 2,
            justifyContent: "flex-end",
          }}
        >
          {!student && (
            <Tooltip title="Generate follow-up quiz" placement="top">
              <span>
                <CreateWithBudButton
                  kind="quiz"
                  assignment={quizAssignment}
                  assignmentQuestion={assignmentQuestion}
                >
                  Ask Bud to create a quiz
                </CreateWithBudButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const FeedbackEditor = ({
  canonicalEntityInsights = [],
  allowGenerate = false,
  student = false,
  excludeTitles = false,
  savedFeedback,
  onRefresh = null,
  onSave,
  rows = 5,
  stretch = false,
  assignmentQuestion = null,
  assignment = null,
  studentLoginView = false,
  answer = null,
}) => {
  const [areasToImprove, setAreasToImprove] = useState(
    savedFeedback?.specificAreasOfImprovement || "",
  );
  const [currentCanonicalEntityInsights, setCurrentCanonicalEntityInsights] =
    useState(canonicalEntityInsights);
  const [specificFeedback, setSpecificFeedback] = useState(
    savedFeedback?.feedback || "",
  );
  const [lastUpdated, setLastUpdated] = useState(
    savedFeedback?.lastUpdated || null,
  );
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(0);
  const ApiClient = useApiClient();

  const [jobId, setJobId] = useState(null);
  useEffect(() => {
    if (cardRef.current) {
      setCardHeight(cardRef.current.clientHeight);
    }
  }, []);

  useEffect(() => {
    if (savedFeedback) {
      setSpecificFeedback(savedFeedback.feedback || "");
      setAreasToImprove(savedFeedback.specificAreasOfImprovement || "");
      setLastUpdated(savedFeedback.lastUpdated || null);
    }
  }, [savedFeedback]);

  const handleSave = () => {
    onSave({
      areasToImprove: areasToImprove,
      specificFeedback: specificFeedback,
    });
  };

  const textRows = stretch
    ? Math.max(rows || 1, Math.floor(cardHeight / 24) - 2)
    : 5;

  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const response = await ApiClient.core.getJobStatus({ jobId });
        if (response.results.length > 0) {
          setAreasToImprove(response.results[0].specificAreasOfImprovement);
          setSpecificFeedback(response.results[0].feedback);
          setLastUpdated(response.results[0].lastUpdated);
        }
        if (response.status === "COMPLETED" || response.status === "FAILED") {
          clearInterval(interval);
          setJobId(null);
        }
      } catch (err) {
        console.error("Error polling job:", err);
        clearInterval(interval);
        setJobId(null);
      }
    }, 2000); // poll every 2s

    return () => clearInterval(interval);
  }, [jobId]);

  const generateOverallFeedback = async () => {
    try {
      const response = await ApiClient.assignment.generateOverallFeedback({
        assignmentQuestionFeedback: assignmentQuestion?.feedback.id,
        assignmentFeedback: assignment?.feedback.id,
      });
      if (response) {
        setJobId(response.job.id);
      }
    } catch (error) {
      console.error("Failed to update Feedback", error);
    }
  };

  const handleAddNewLearningSignal = async (
    entity,
    selectedEntityType,
    reasoning,
  ) => {
    try {
      const response = await ApiClient.skill.addLearningSignal({
        canonicalEntity: entity?.id,
        entityType: selectedEntityType,
        reasoning: reasoning,
        answer: answer?.id,
      });
      if (response.success) {
        setCurrentCanonicalEntityInsights(response.canonicalEntityInsights);
        enqueueSnackbar("Successfully added new evidence", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar("Failed to add new evidence", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to add new evidence", error);
    }
  };

  const handleAddNewEntityLink = async (entity, reasoning) => {
    try {
      const response = await ApiClient.skill.linkCanonicalEntity({
        canonicalEntity: entity.id,
        reasoning: reasoning,
        assignmentFeedback: assignmentQuestion ? null : savedFeedback.id,
        assignmentQuestionFeedback: assignmentQuestion
          ? savedFeedback.id
          : null,
      });
      if (response.success) {
        setCurrentCanonicalEntityInsights(response.canonicalEntityInsights);
        enqueueSnackbar("Successfully added new evidence", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar("Failed to add new evidence", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to add new evidence", error);
    }
  };

  const handleNewEntityLink = async ({
    entity,
    selectedEntityType,
    reasoning,
  }) => {
    if (student) {
      handleAddNewLearningSignal(entity, selectedEntityType, reasoning);
    } else {
      handleAddNewEntityLink(entity, reasoning);
    }
  };

  if (jobId) {
    return (
      <Box sx={{ flex: 1, mt: stretch ? 0 : 2 }} ref={cardRef}>
        <FruitifyLoading category={MESSAGE_CATEGORIES.feedback} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, mt: stretch ? 0 : 2 }} ref={cardRef}>
      {!excludeTitles && (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box>
            {studentLoginView ? (
              <Typography variant={"h5"}>Question feedback</Typography>
            ) : (
              <>
                <Typography variant={"h5"}>
                  {student ? "Student feedback" : "Class-level feedback"}
                </Typography>
                <Typography variant={"subtitle1"}>
                  Notes on what students did well and what they need to focus on
                  to improve. This helps identify learning gaps and teaching
                  opportunities.
                </Typography>
              </>
            )}
          </Box>

          {!student && !studentLoginView && (
            <Stack direction={"column"} alignItems={"flex-end"}>
              <Tooltip title={!allowGenerate ? "More answers required" : ""}>
                <span>
                  <Button
                    startIcon={<FruitifyIcon name={"UseAI"} />}
                    onClick={generateOverallFeedback}
                    variant={"contained"}
                    disabled={!allowGenerate}
                  >
                    Generate
                  </Button>
                </span>
              </Tooltip>
              {(specificFeedback || areasToImprove) && (
                <Typography variant={"body2"}>
                  Last updated{" "}
                  {formatDistanceToNow(new Date(lastUpdated), {
                    addSuffix: true,
                  })}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      )}
      <Stack direction={{ sm: "column", md: "row" }} spacing={2}>
        <FeedbackEditorTextfield
          label={"What went well"}
          helperText={"Summarise common strengths or positive trends"}
          value={specificFeedback}
          onChange={setSpecificFeedback}
          onSave={handleSave}
          rows={textRows}
          readOnly={studentLoginView}
        />
        <FeedbackEditorTextfield
          label={"Overall areas to improve"}
          helperText={"Highlight recurring issues or areas needing support"}
          value={areasToImprove}
          onChange={setAreasToImprove}
          onSave={handleSave}
          rows={textRows}
          readOnly={studentLoginView}
        />
      </Stack>
      {!studentLoginView && (
        <CanonicalEntityInsights
          canonicalEntityInsights={currentCanonicalEntityInsights}
          assignment={assignment}
          assignmentQuestion={assignmentQuestion}
          student={student}
          onNewEntityLink={handleNewEntityLink}
          onRefresh={onRefresh}
        />
      )}
    </Box>
  );
};
